import React, { useState, useEffect, useContext } from 'react';
import { Search, Filter, Eye, Edit, Trash2, X } from 'lucide-react';
import TipTapEditor from "../common/TipTapEditor";
import { OpportunityService } from '../../services/OpportunityService';
import { useSnackBar } from '../../hooks/useSnackBar';
import { FaUserGraduate, FaUsers, FaUserTie, FaCalendarAlt, FaSearch, FaEye, FaEdit, FaTrash, FaUniversity, FaMapPin, FaDollarSign, FaFileAlt, FaLink, FaCity, FaGlobe, FaEnvelope, FaMagic, FaStar, FaLock, FaUser, FaGraduationCap } from "react-icons/fa";
import { AuthContext } from '../../context/AuthContext';
import { encodeImageUrl } from '../../utils/helpers';
import { canManageOpportunities } from '../../utils/permissions';
import OpportunityGrid from '../OpportunityGrid/OpportunityGrid';

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const opportunitiesPerPage = 30;

// Modal for viewing opportunity details
export function OpportunityDetailsModal({ opportunity, onClose, loading }) {
  if (!opportunity && !loading) return null;
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-[#FFD6D4] w-[90%] max-w-4xl max-h-[90vh] flex flex-col relative overflow-hidden ml-20">
        {/* Header with close button */}
        <div className="flex items-center justify-between p-6 border-b border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h1 className="text-2xl font-bold text-[#DB4F40]">Opportunity Details</h1>
          <button
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={onClose}
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content area with scroll */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* University and location section */}
              <div className="flex flex-col lg:flex-row gap-6 items-start">
                <div className="flex-shrink-0 bg-white p-4 rounded-2xl shadow-lg border-2 border-[#FFD6D4] flex items-center justify-center min-w-[120px]">
                  <img
                    src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''}/university/${encodeImageUrl(opportunity.university)}.png`}
                    alt={opportunity.university}
                    className="h-24 w-24 object-contain"
                    onError={e => (e.target.style.display = 'none')}
                  />
                  {!opportunity.university && (
                    <FaUniversity className="h-24 w-24 text-[#DB4F40]/30 p-4" />
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-[#1B222A] mb-3">{opportunity.university}</h2>
                  <div className="flex items-center text-[#1B222A]/80 space-x-3 mb-2">
                    <FaMapPin className="text-[#DB4F40] flex-shrink-0 text-lg" />
                    <span className="text-xl font-medium">{opportunity.city}, {opportunity.country}</span>
                  </div>
                  <div className="text-lg text-[#1B222A]/70 font-medium">{opportunity.department}</div>
                </div>
              </div>

              {/* Title */}
              <div className="bg-gradient-to-r from-[#FFEEED] to-white p-6 rounded-2xl border border-[#FFD6D4]">
                <h1 className="text-3xl font-bold text-[#DB4F40] leading-tight">{opportunity.title}</h1>
              </div>

              {/* Key details grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {opportunity.supervisor && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaUserTie className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Supervisor</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">{opportunity.supervisor}</span>
                  </div>
                )}
                {opportunity.closing_date && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaCalendarAlt className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Deadline</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">{formatDate(opportunity.closing_date)}</span>
                  </div>
                )}
                {opportunity.fully_funded !== undefined && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4] hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3">
                      <FaDollarSign className="text-[#DB4F40] mr-2 text-lg" />
                      <span className="font-bold text-[#1B222A] text-lg">Funding</span>
                    </div>
                    <span className="text-[#1B222A]/80 text-base">
                      {opportunity.fully_funded ? 'Fully Funded' : 'Partial/Not Funded'}
                    </span>
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#FFD6D4]">
                <h3 className="text-xl font-bold text-[#DB4F40] mb-4 flex items-center">
                  <FaFileAlt className="mr-2" />
                  Description
                </h3>
                <div className="prose prose-lg max-w-none text-[#1B222A]/90 leading-relaxed">
                  <div dangerouslySetInnerHTML={{ __html: opportunity.description }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Edit Opportunity Modal Component
export function EditOpportunityModal({ opportunity, onClose, onSubmit }) {
  const [focusedField, setFocusedField] = useState(null);
  const [formData, setFormData] = useState({
    category: "standard",
    title: "",
    department: "",
    university: "",
    university_url: "",
    city: "",
    country: "",
    closing_date: "",
    open_to: "",
    supervisor: "",
    supervisor_url: "",
    type: "PhD",
    fully_funded: false,
    description: "",
    status: "pending"
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data when opportunity changes
  useEffect(() => {
    if (opportunity) {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      try {
        // Convert Unix timestamp to date string for the date input
        const closingDate = opportunity.closing_date 
          ? new Date(opportunity.closing_date * 1000).toISOString().split('T')[0]
          : '';
        
        const newFormData = {
          category: opportunity.category || "standard",
          title: opportunity.title || "",
          department: opportunity.department || "",
          university: opportunity.university || "",
          university_url: opportunity.university_url || "",
          city: opportunity.city || "",
          country: opportunity.country || "",
          closing_date: closingDate,
          open_to: opportunity.open_to || "",
          supervisor: opportunity.supervisor || "",
          supervisor_url: opportunity.supervisor_url || "",
          type: opportunity.type || "PhD",
          fully_funded: opportunity.fully_funded || false,
          description: opportunity.description ? decodeHtmlEntities(opportunity.description) : "",
          status: opportunity.status || "pending"
        };
        
        setFormData(newFormData);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load opportunity data. Please try again.');
        setLoading(false);
        console.error('Error loading opportunity data:', err);
      }
    }
  }, [opportunity]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      description: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null); // Clear any previous errors
    
    try {
      const closingDateEpoch = formData?.closing_date
        ? Math.floor(
            Date.UTC(
              ...formData.closing_date
                .split("-")
                .map((v, i) => (i === 1 ? Number(v) - 1 : Number(v)))
            ) / 1000
          )
        : null;

      const closingDateString = closingDateEpoch ? closingDateEpoch.toString() : null;

      await onSubmit({
        ...formData,
        closing_date: closingDateString,
        fully_funded: formData?.fully_funded === true,
      });
      
      onClose();
    } catch (error) {
      console.error('Edit submission error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update opportunity. Please try again.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const fieldConfigs = [
    { name: "university", icon: FaUniversity, placeholder: "e.g., Stanford University" },
    { name: "university_url", icon: FaLink, placeholder: "https://university.edu" },
    { name: "city", icon: FaCity, placeholder: "e.g., Palo Alto" },
    { name: "country", icon: FaGlobe, placeholder: "e.g., United States" },
    { name: "title", icon: FaUserGraduate, placeholder: "e.g., Machine Learning in Healthcare PhD Position" },
    { name: "department", icon: FaUsers, placeholder: "e.g., Computer Science Department" },
    { name: "supervisor", icon: FaUserTie, placeholder: "e.g., Prof. Jane Smith" },
    { name: "supervisor_url", icon: FaEnvelope, placeholder: "e.g., prof@university.edu or https://linkedin.com/in/prof" },
    { name: "open_to", icon: FaUsers, placeholder: "e.g., International students, EU citizens" },
    { name: "closing_date", icon: FaCalendarAlt, placeholder: "" },
  ];

  const requiredFields = ["title", "university", "department", "city", "country", "closing_date"];

      return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="bg-white rounded-3xl shadow-2xl border border-[#FFD6D4] w-[90%] max-w-3xl max-h-[90vh] flex flex-col relative overflow-hidden ml-20">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#FFD6D4] bg-gradient-to-r from-[#FFEEED] to-white">
          <h1 className="text-2xl font-bold text-[#DB4F40]">Edit Opportunity</h1>
          <button
            className="flex items-center justify-center w-10 h-10 rounded-full bg-[#DB4F40] text-white hover:bg-[#C64537] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            onClick={onClose}
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content area with scroll */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#FFD6D4] border-t-[#DB4F40]"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 max-w-md">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-red-800">Error Loading Data</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
            {/* Form Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {fieldConfigs.map((field) => {
                const Icon = field.icon;
                const isRequired = requiredFields.includes(field.name);
                const isFocused = focusedField === field.name;
                
                if (field.name === "closing_date") {
                  return (
                    <div key={field.name} className="md:col-span-2 flex flex-col md:flex-row md:items-end gap-4">
                      <div className="flex-1">
                        <label className="flex items-center text-sm font-semibold text-[#1B222A] mb-3">
                          <Icon className="w-4 h-4 mr-2 text-[#DB4F40]" />
                          Deadline
                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <div className={`relative group transition-all duration-300 ${isFocused ? 'transform scale-105' : ''}`}> 
                          <input
                            type="date"
                            name={field.name}
                            value={formData[field.name]}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField(field.name)}
                            onBlur={() => setFocusedField(null)}
                            className="w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md"
                            required={isRequired}
                            placeholder={field.placeholder}
                          />
                          <div className="absolute inset-0 rounded-xl bg-[#FFD6D4] opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                        </div>
                      </div>
                      {/* Funding Status Checkbox */}
                      <div className="flex items-center space-x-3 md:mb-0 md:ml-6 mb-0 h-full" style={{ alignSelf: 'center' }}>
                        <input
                          type="checkbox"
                          name="fully_funded"
                          checked={formData.fully_funded}
                          onChange={handleInputChange}
                          id="fully_funded"
                          className="h-5 w-5 text-[#DB4F40] border-[#FFD6D4] rounded focus:ring-[#DB4F40]"
                        />
                        <label htmlFor="fully_funded" className="text-[#DB4F40] font-medium select-none">
                          Fully Funded
                        </label>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div key={field.name} className={field.name === "title" ? "md:col-span-2" : ""}>
                    <label className="flex items-center text-sm font-semibold text-[#1B222A] mb-3">
                      <Icon className="w-4 h-4 mr-2 text-[#DB4F40]" />
                      {field.name === "supervisor_url"
                        ? "Supervisor email/LinkedIn URL"
                        : field.name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <div className={`relative group transition-all duration-300 ${isFocused ? 'transform scale-105' : ''}`}> 
                      <input
                        type={field.name === "supervisor_url" ? "text" : (field.name.includes("url") ? "url" : "text")}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        onFocus={() => setFocusedField(field.name)}
                        onBlur={() => setFocusedField(null)}
                        className="w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md"
                        required={isRequired}
                        placeholder={field.placeholder}
                      />
                      <div className="absolute inset-0 rounded-xl bg-[#FFD6D4] opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none"></div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Description Section */}
            <div className="bg-[#FFD6D4] rounded-2xl p-6 border border-[#FFD6D4]">
              <label className="flex items-center text-sm font-semibold text-[#1B222A] mb-4">
                <FaFileAlt className="w-4 h-4 mr-2 text-[#DB4F40]" />
                Opportunity Description
              </label>
              
              <TipTapEditor
                key={opportunity?.id || 'new'}
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder="✨ Describe the research opportunity, requirements, benefits, and what makes this position special..."
              />
              
              <div className="mt-4 flex items-center text-xs text-[#1B222A] opacity-80">
                <FaStar className="w-3 h-3 mr-1 text-[#DB4F40]" />
                <span>Pro tip: Include research focus, required skills, application process, and what the candidate will learn</span>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-[#DB4F40] hover:bg-[#c13426] text-white px-8 py-4 rounded-2xl font-semibold text-lg shadow-xl hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-[#FFD6D4] transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <div className="flex items-center justify-center">
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Updating Opportunity...
                    </>
                  ) : (
                    <>
                      <FaMagic className="h-5 w-5 mr-3 text-[#FFD6D4]" />
                      Update Opportunity
                      <FaStar className="h-4 w-4 ml-2 text-[#FFD6D4]" />
                    </>
                  )}
                </div>
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}

const SaasEditOpportunitySection = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    title: '',
    department: '',
    supervisor: '',
    createdAtFrom: '',
    createdAtTo: '',
    deadlineFrom: '',
    deadlineTo: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [customerInfo, setCustomerInfo] = useState(null);
  const enqMsg = useSnackBar();
  const [pagination, setPagination] = useState({ page: 1, limit: 30, total: 0 });
  const [pendingFilters, setPendingFilters] = useState({
    title: '',
    department: '',
    supervisor: '',
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, opportunity: null });
  const { userDetails: user } = useContext(AuthContext);

  useEffect(() => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    // Fetch opportunities
    const fetchOpportunities = async () => {
      setLoading(true);
      try {
        const params = {
          title: filters.title,
          department: filters.department,
          supervisor: filters.supervisor,
          ...(filters.createdAtFrom && { "created_at[gt]": filters.createdAtFrom }),
          ...(filters.createdAtTo && { "created_at[lt]": filters.createdAtTo }),
          ...(filters.deadlineFrom && { "closing_date[gt]": filters.deadlineFrom }),
          ...(filters.deadlineTo && { "closing_date[lt]": filters.deadlineTo }),
          limit: pagination.limit,
          offset: (pagination.page - 1) * pagination.limit,
        };
        const response = await OpportunityService.getSaasOpportunities(params);
        setOpportunities(response.data || []);
        setPagination((prev) => ({ ...prev, total: response.pagination?.total || 0 }));
      } catch (error) {
        console.error('Error fetching opportunities:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch opportunities. Please try again.';
        enqMsg(errorMessage, 'error');
        setOpportunities([]);
      } finally {
        setLoading(false);
      }
    };
    // Fetch customer info
    const fetchCustomerInfo = async () => {
      try {
        const response = await OpportunityService.getCurrentCustomer();
        setCustomerInfo(response);
      } catch (error) {
        // ignore
      }
    };
    fetchOpportunities();
    fetchCustomerInfo();
  }, [filters, pagination.page]);

  // Remove client-side filtering: use only backend results
  const paginatedOpportunities = opportunities;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setCurrentPage(1); // Reset to first page on filter change
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString();
  };

  const getFundingText = (fullyFunded) => {
    return fullyFunded ? 'Full funding available' : 'Partial funding';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      'rejected': { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };
    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  // View handler: fetch from backend
  const handleViewOpportunity = async (opportunity) => {
    setDetailsLoading(true);
    setShowDetailsModal(true);
    try {
      const response = await OpportunityService.getSaasOpportunityById(opportunity.id);
      setSelectedOpportunity(response.data);
    } catch (error) {
      console.error('Error fetching opportunity details:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch opportunity details.';
      enqMsg(errorMessage, 'error');
      setShowDetailsModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Delete handler: call backend (validation handled by backend)
  const handleDeleteOpportunity = async (opportunity) => {
    if (!window.confirm('Are you sure you want to delete this opportunity?')) return;
    try {
      await OpportunityService.deleteSaasOpportunityById(opportunity.id);
      enqMsg('Opportunity deleted successfully.', 'success');
      // Refresh list
      setOpportunities((prev) => prev.filter((o) => o.id !== opportunity.id));
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete opportunity. Please try again.';
      enqMsg(errorMessage, 'error');
    }
  };

  // Edit handler: open modal
  // Edit handler: fetch full opportunity data and open modal
  const handleEditOpportunity = async (opportunity) => {
    try {
      // Fetch the complete opportunity data from backend
      const response = await OpportunityService.getSaasOpportunityById(opportunity.id);
      const fullOpportunityData = response.data;
      
      // Open modal with complete data
      setEditModal({ open: true, opportunity: fullOpportunityData });
    } catch (error) {
      console.error('Error fetching opportunity details:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch opportunity details for editing.';
      enqMsg(errorMessage, 'error');
    }
  };

  // Edit submit handler
  const handleEditSubmit = async (updatedData) => {
    try {
      await OpportunityService.updateSaasOpportunityById(editModal.opportunity.id, updatedData);
      enqMsg('Opportunity updated successfully.', 'success');
      setEditModal({ open: false, opportunity: null });
      
      // Refresh the opportunities list
      const params = {
        title: filters.title,
        department: filters.department,
        supervisor: filters.supervisor,
        ...(filters.createdAtFrom && { "created_at[gt]": filters.createdAtFrom }),
        ...(filters.createdAtTo && { "created_at[lt]": filters.createdAtTo }),
        ...(filters.deadlineFrom && { "closing_date[gt]": filters.deadlineFrom }),
        ...(filters.deadlineTo && { "closing_date[lt]": filters.deadlineTo }),
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      };
      const response = await OpportunityService.getSaasOpportunities(params);
      setOpportunities(response.data || []);
      setPagination((prev) => ({ ...prev, total: response.pagination?.total || 0 }));
    } catch (error) {
      console.error('Error updating opportunity:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update opportunity. Please try again.';
      enqMsg(errorMessage, 'error');
      // Re-throw the error so the modal can handle it
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFEEED] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto mb-8">
        <h1 className="text-3xl font-extrabold text-[#DB4F40] mb-6">Manage Opportunities</h1>
      </div>
      <div className="relative max-w-6xl mx-auto">
        <div className="p-8 sm:p-12">
          {/* Filter Section */}
          <div className="max-w-6xl mx-auto">
            <div className="overflow-hidden mb-8 rounded-3xl border border-[#FFD6D4] bg-white">
              <div className="p-8 sm:p-12">
                <h2 className="text-2xl font-bold text-[#DB4F40] mb-6">Filter Opportunities</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#1B222A] mb-2">Title</label>
                    <div className="relative">
                      <FaUserGraduate className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                      <input type="text" name="title" value={pendingFilters.title} onChange={e => setPendingFilters(f => ({ ...f, title: e.target.value }))} placeholder="Filter by Title" className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1B222A] mb-2">Department</label>
                    <div className="relative">
                      <FaUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                      <input type="text" name="department" value={pendingFilters.department} onChange={e => setPendingFilters(f => ({ ...f, department: e.target.value }))} placeholder="Filter by Department" className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1B222A] mb-2">Supervisor</label>
                    <div className="relative">
                      <FaUserTie className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#DB4F40] w-4 h-4" />
                      <input type="text" name="supervisor" value={pendingFilters.supervisor} onChange={e => setPendingFilters(f => ({ ...f, supervisor: e.target.value }))} placeholder="Filter by Supervisor" className="w-full pl-10 pr-4 py-2 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md" />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => {
                      setFilters({ ...pendingFilters });
                      setPagination(p => ({ ...p, page: 1 }));
                    }}
                    className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm mr-2"
                    type="button"
                  >
                    Search
                  </button>
                  <button
                    onClick={() => {
                      setPendingFilters({ title: '', department: '', supervisor: '' });
                      setFilters({ title: '', department: '', supervisor: '' });
                      setPagination(p => ({ ...p, page: 1 }));
                    }}
                    className="px-6 py-2 rounded-lg bg-[#DB4F40] text-white font-semibold hover:bg-[#C64537] transition-all shadow-sm"
                    type="button"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
            {/* Data Section (bottom card) */}
            <div className="overflow-hidden rounded-3xl border border-[#FFD6D4] bg-white">
              <div className="p-8 sm:p-12">
                <h2 className="text-2xl font-bold text-[#DB4F40] mb-6">Opportunities List</h2>
                {paginatedOpportunities.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    No opportunities found. Try adjusting your filters.
                  </div>
                ) : (
                  window.innerWidth < 768 ? (
                    <OpportunityGrid opportunities={paginatedOpportunities} customClassName="grid grid-cols-1 gap-6 w-full px-2" />
                  ) : (
                    <div className="grid grid-cols-1 gap-8">
                      {paginatedOpportunities.map((opportunity) => (
                        <div key={opportunity.id} className="relative">
                          <div
                            className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 group mx-[5%] relative ${window.innerWidth < 768 ? 'border-2 border-[#FFD6D4]' : 'border-4 border-[#FFD6D4] hover:border-[#DB4F40]'}`}
                          >
                            <div className="p-6 flex flex-col md:flex-row gap-6">
                              {/* University Logo */}
                              <div className="flex-shrink-0 flex flex-col justify-center items-center h-full md:h-auto md:justify-center bg-gradient-to-br from-[#FFBEB9] to-[#FFD1CE] rounded-xl" style={{ flex: '0 0 15%' }}>
                                <div className="w-24 h-24 flex-shrink-0 flex items-center justify-center">
                                  <img
                                    src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : ''}/university/${opportunity.university.replace(/ /g, '%20')}.png`}
                                    alt={opportunity.university}
                                    className="w-full h-full object-contain transition-opacity duration-300 hover:opacity-80"
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              </div>
                              {/* Main Content */}
                              <div className="flex-1 min-w-0 flex flex-col gap-2">
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                  <h3 className="text-lg font-bold text-[#1B222A] hover:text-[#DB4F40] transition-colors cursor-pointer group-hover:text-[#DB4F40]">
                                    {opportunity.title}
                                  </h3>
                                  {opportunity.fully_funded && (
                                    <div className="flex flex-wrap gap-2">
                                      <span className="px-3 py-1 bg-[#5AC1E4]/20 text-[#1B222A] rounded-full text-xs font-medium">
                                        Fully Funded
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-base">
                                  <div className="flex items-center">
                                    <FaUniversity className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{opportunity.university}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <FaGraduationCap className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{opportunity.department}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <FaUserTie className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{opportunity.supervisor || 'N/A'}</span>
                                  </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-base">
                                  <div className="flex items-center">
                                    <FaCalendarAlt className="text-[#DB4F40] mr-2" />
                                    <span className="font-semibold text-[#6C757D]">{formatDate(opportunity.closing_date)}</span>
                                  </div>
                                </div>
                                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
                                  <button
                                    className="flex items-center px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#C64537] transition-colors text-sm font-medium"
                                    onClick={() => handleViewOpportunity(opportunity)}
                                    title="View Opportunity"
                                  >
                                    <FaEye className="w-4 h-4 mr-2" />
                                    View
                                  </button>
                                  {canManageOpportunities(currentUser) && (
                                    <>
                                      <button
                                        className="flex items-center px-4 py-2 bg-[#FFD6D4] text-[#DB4F40] rounded-lg hover:bg-[#DB4F40] hover:text-white transition-colors text-sm font-medium"
                                        onClick={() => handleEditOpportunity(opportunity)}
                                        title="Edit Opportunity"
                                      >
                                        <FaEdit className="w-4 h-4 mr-2" />
                                        Edit
                                      </button>
                                      <button
                                        className="flex items-center px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-colors text-sm font-medium"
                                        onClick={() => handleDeleteOpportunity(opportunity)}
                                        title="Delete Opportunity"
                                      >
                                        <FaTrash className="w-4 h-4 mr-2" />
                                        Delete
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
                {/* Pagination Controls */}
                {pagination.total > pagination.limit && (
                  <div className="flex flex-col sm:flex-row justify-center items-center gap-4 py-8">
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="px-6 py-2 rounded-lg bg-white border border-[#FFD6D4] hover:border-[#DB4F40] text-[#DB4F40] hover:bg-[#FFEEED] disabled:opacity-50 transition-all"
                    >
                      Previous
                    </button>
                    <span className="px-6 py-2 bg-[#DB4F40] text-white rounded-lg shadow-sm">
                      Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
                    </span>
                    <button
                      onClick={() => setPagination(p => ({ ...p, page: Math.min(Math.ceil(pagination.total / pagination.limit), p.page + 1) }))}
                      disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                      className="px-6 py-2 rounded-lg bg-white border border-[#FFD6D4] hover:border-[#DB4F40] text-[#DB4F40] hover:bg-[#FFEEED] disabled:opacity-50 transition-all"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDetailsModal && (
        <OpportunityDetailsModal
          opportunity={selectedOpportunity}
          loading={detailsLoading}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
      {/* Edit Opportunity Modal */}
      {editModal.open && (
        <EditOpportunityModal
          opportunity={editModal.opportunity}
          onClose={() => setEditModal({ open: false, opportunity: null })}
          onSubmit={handleEditSubmit}
        />
      )}
    </div>
  );
};

export default SaasEditOpportunitySection;